import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { patchMediaSettings } from "../../../../page-controller-mod/page-serv/settings/media-management-set/ts/setting-digital.type";
import { buildAnalyticsPayload } from "./build-analytic-payload";
import { buildJobAdminPayload } from "./build-job-payload";
import { buildSubmissionPayload } from "./build-submission-payload";
import { buildNotificationPayload } from "./build-notification-payload";
import { buildAttachmentPayload } from "./build-attachment-payload";
import { patchAnalyticsConfiguration } from "../../../../page-controller-mod/page-serv/settings/analytic-set/ts/setting-analytic.type";
import { patchJobAdministratorSettings } from "../../../../page-controller-mod/page-serv/settings/application-set/ts/setting-applicaation.type";
import { patchSubmissionSettings } from "../../../../page-controller-mod/page-serv/settings/general-set/ts/setting-submission.type";
import { patchNotificationSettings } from "../../../../page-controller-mod/page-serv/settings/notification-set/ts/setting-notification.type";
import { patchAttachmentSettings } from "../../../../page-controller-mod/page-serv/settings/workflow-set/ts/setting-attachment.type";
import { buildMediaPayload } from "./build-media-mgt-payload";

type ToggleMap = Record<string, boolean>;
type InputMap = Record<string, string>;

type ChangeType = "toggle" | "input";

export type SettingChange = {
  pageKey: string;
  groupId: number;
  itemId: number;
  itemTitle?: string;
  groupTitle?: string;
  type: ChangeType;
  value: boolean | string;
};

type DraftState = {
  toggles: ToggleMap;
  inputs: InputMap;
  changes: Record<string, SettingChange>;
};

type SaveAllResult = {
  didPatch: boolean;
  message: string;
};

type SettingsDraftCtx = {
  isDirty: boolean;

  hydrateMany: (payload: HydrateManyPayload) => void;

  getToggle: (pageKey: string, groupId: number, itemId: number) => boolean;
  getInput: (pageKey: string, groupId: number, itemId: number) => string;

  setToggle: (c: Omit<SettingChange, "type" | "value"> & { value: boolean }) => void;
  setInput: (c: Omit<SettingChange, "type" | "value"> & { value: string }) => void;

  hydrateToggle: (pageKey: string, groupId: number, itemId: number, value: boolean) => void;
  hydrateInput: (pageKey: string, groupId: number, itemId: number, value: string) => void;

  saveToConsole: () => void;
  resetDraft: () => void;

  saveAll: () => Promise<SaveAllResult>;
};

type HydrateManyPayload = {
  pageKey: string;
  toggles?: Array<{ groupId: number; itemId: number; value: boolean }>;
  inputs?: Array<{ groupId: number; itemId: number; value: string }>;
};

const SettingsDraftContext = createContext<SettingsDraftCtx | null>(null);

const makeKey = (pageKey: string, groupId: number, itemId: number) => `${pageKey}:${groupId}:${itemId}`;
const changeKey = (pageKey: string, groupId: number, itemId: number, type: ChangeType) =>
  `${type}:${pageKey}:${groupId}:${itemId}`;

export const SettingsDraftProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<DraftState>({
    toggles: {},
    inputs: {},
    changes: {},
  });

  const getToggle = useCallback(
    (pageKey: string, groupId: number, itemId: number) => state.toggles[makeKey(pageKey, groupId, itemId)] ?? false,
    [state.toggles]
  );

  const getInput = useCallback(
    (pageKey: string, groupId: number, itemId: number) => state.inputs[makeKey(pageKey, groupId, itemId)] ?? "",
    [state.inputs]
  );

  const setToggle: SettingsDraftCtx["setToggle"] = useCallback((c) => {
    const mapKey = makeKey(c.pageKey, c.groupId, c.itemId);
    const cKey = changeKey(c.pageKey, c.groupId, c.itemId, "toggle");

    setState((prev) => ({
      ...prev,
      toggles: { ...prev.toggles, [mapKey]: c.value },
      changes: {
        ...prev.changes,
        [cKey]: { ...c, type: "toggle", value: c.value },
      },
    }));
  }, []);

  const setInput: SettingsDraftCtx["setInput"] = useCallback((c) => {
    const mapKey = makeKey(c.pageKey, c.groupId, c.itemId);
    const cKey = changeKey(c.pageKey, c.groupId, c.itemId, "input");

    setState((prev) => ({
      ...prev,
      inputs: { ...prev.inputs, [mapKey]: c.value },
      changes: {
        ...prev.changes,
        [cKey]: { ...c, type: "input", value: c.value },
      },
    }));
  }, []);

  const hydrateToggle = useCallback((pageKey: string, groupId: number, itemId: number, value: boolean) => {
    const mapKey = makeKey(pageKey, groupId, itemId);
    setState((prev) => ({
      ...prev,
      toggles: { ...prev.toggles, [mapKey]: value },
    }));
  }, []);

  const hydrateInput = useCallback((pageKey: string, groupId: number, itemId: number, value: string) => {
    const mapKey = makeKey(pageKey, groupId, itemId);
    setState((prev) => ({
      ...prev,
      inputs: { ...prev.inputs, [mapKey]: value },
    }));
  }, []);

  const hydrateMany = useCallback((payload: HydrateManyPayload) => {
    setState((prev) => {
      const nextToggles = { ...prev.toggles };
      const nextInputs = { ...prev.inputs };

      for (const t of payload.toggles ?? /* istanbul ignore next */ []) {
        const mapK = makeKey(payload.pageKey, t.groupId, t.itemId);
        const cK = changeKey(payload.pageKey, t.groupId, t.itemId, "toggle");

        //  if user already changed it, keep the draft value (don’t override)
        if (prev.changes[cK]) continue;

        nextToggles[mapK] = t.value;
      }

      for (const i of payload.inputs ?? /* istanbul ignore next */ []) {
        const mapK = makeKey(payload.pageKey, i.groupId, i.itemId);
        const cK = changeKey(payload.pageKey, i.groupId, i.itemId, "input");

        //  same for inputs
        if (prev.changes[cK]) continue;

        nextInputs[mapK] = i.value;
      }

      return {
        ...prev,
        toggles: nextToggles,
        inputs: nextInputs,
      };
    });
  }, []);

  const saveToConsole = useCallback(() => {
    const list = Object.values(state.changes);

    // pretty output
    console.groupCollapsed(`Settings Save: ${list.length} change(s)`);
    console.table(
      list.map((c) => ({
        page: c.pageKey,
        groupId: c.groupId,
        groupTitle: c.groupTitle ?? /* istanbul ignore next */ "",
        itemId: c.itemId,
        itemTitle: c.itemTitle ?? /* istanbul ignore next */ "",
        type: c.type,
        value: c.value,
      }))
    );
  }, [state.changes]);

  const saveAll = useCallback(async () => {
    const list = Object.values(state.changes);

    //  Build all payloads (you already have these in separate files)
    const mediaBody = buildMediaPayload(list);
    const analyticBody = buildAnalyticsPayload(list);
    const jobBody = buildJobAdminPayload(list);
    const submissionBody = buildSubmissionPayload(list);
    const notificationBody = buildNotificationPayload(list);
    const attachmentBody = buildAttachmentPayload(list);

    const tasks: Promise<any>[] = [];

    if (Object.keys(mediaBody).length) tasks.push(patchMediaSettings(mediaBody));
    if (Object.keys(analyticBody).length) tasks.push(patchAnalyticsConfiguration(analyticBody));
    if (Object.keys(jobBody).length) tasks.push(patchJobAdministratorSettings(jobBody));
    if (Object.keys(submissionBody).length) tasks.push(patchSubmissionSettings(submissionBody));
    if (Object.keys(notificationBody).length) tasks.push(patchNotificationSettings(notificationBody));
    if (Object.keys(attachmentBody).length) tasks.push(patchAttachmentSettings(attachmentBody));

    if (tasks.length === 0) {
      return { didPatch: false, message: "No patchable changes." };
    }

    await Promise.all(tasks);

    //  clear changes after success
    setState((prev) => ({ ...prev, changes: {} }));

    return { didPatch: true, message: `Saved ${tasks.length} setting module(s).` };
  }, [state.changes]);

  const resetDraft = useCallback(() => {
    setState({ toggles: {}, inputs: {}, changes: {} });
  }, []);

  const value = useMemo<SettingsDraftCtx>(
    () => ({
      isDirty: Object.keys(state.changes).length > 0,
      getToggle,
      getInput,
      setToggle,
      setInput,
      hydrateToggle,
      hydrateInput,
      hydrateMany,
      saveToConsole,
      resetDraft,
      saveAll
    }),
    [getToggle, getInput, setToggle, setInput, hydrateToggle, hydrateInput, hydrateMany, saveToConsole, resetDraft, saveAll, state.changes]
  );

  return <SettingsDraftContext.Provider value={value}>{children}</SettingsDraftContext.Provider>;
};

export const useSettingsDraft = () => {
  const ctx = useContext(SettingsDraftContext);
  if (!ctx) throw new Error("useSettingsDraft must be used inside SettingsDraftProvider");
  return ctx;
};